import React from "react";

import SupportEscalationScreen from "@/b2b/components/SupportEscalationScreen";

/**
 * Shown when a tenant has used every bank check allowed on their account.
 *
 * A true dead end: they cannot retry, and only ops can move them (raise the limit, or run the
 * check themselves). So the screen escalates on arrival and simply reports what is happening,
 * rather than asking them to press a button to request help they obviously need.
 *
 * Copy notes — this wording went through several rounds, so the constraints are worth keeping:
 *
 *   • Short sentences, no idioms. "Didn't go through", "we're on it", "taken it from here" and
 *     "get in touch" all read fine to us and land badly on a tenant reading English as a second
 *     or third language. "Verification" became "check your bank details" for the same reason.
 *
 *   • It NAMES the problem. The most common thing a stuck tenant does is photograph this screen
 *     and send it to us; copy that only says "our team is helping you" makes ops open a ticket
 *     to find out with what. "All your bank check tries are used" is the line that tells them.
 *
 *   • But with nobody doing the using. "You have used all your tries" is the same fact aimed at
 *     the tenant like a scolding, for something that is now ours to finish. The passive states
 *     it and blames no one, and the very next sentence hands the work to us so the fact never
 *     sits on screen alone.
 *
 *   • It promises to TELL them, not to call them. "We will call you soon" commits ops to a
 *     channel and a timeframe neither this screen nor the alert behind it can guarantee, and an
 *     unmet promise is what turns a patient tenant into an angry one. "We will tell you when it
 *     is done" is true whichever way we reach them.
 *
 *   • The failure copy promises nothing at all, because in that state nobody has been told yet.
 */
export default function BankingLimitExceededScreen({ theme }) {
  return (
    <SupportEscalationScreen
      theme={theme}
      topic="banking-limit"
      reason="Banking attempt limit reached"
      problemLine="I've used all my bank verification attempts and can't continue on my own."
      copy={{
        idle: {
          title: "Please wait",
          body: "We are checking your details.",
        },
        sent: {
          title: "We are helping you",
          body: "All your bank check tries are used. Our team will now check your bank details for you. We will tell you when it is done. You do not need to do anything.",
        },
        failed: {
          title: "We are here to help",
          body: "All your bank check tries are used. Please message us or call us. We will help you.",
        },
      }}
    />
  );
}
